import { useState, MouseEvent } from "react";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import FolderIcon from "@mui/icons-material/Folder";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import FileIcon from "../abstractComponents/fileIcon";
import { BpCheckbox, StyledTreeItem } from "../../helpers/treeHelper";
import {
  ExtendedRepositoryObject,
  RepositoryObject,
  RepositoryObjectFolder,
  RepositoryTree
} from "../../backend/types";

type InteractivePartialImportProps = {
  actionId?: string;
  callRepositoryObjects: Function;
  foldersAndCount: RepositoryObjectFolder[];
  repositoryObjects: RepositoryObject[];
  setSelectedFileImport: Function;
  selectedFileImport: ExtendedRepositoryObject[];
  tree: RepositoryTree[];
};

export function InteractivePartialImport({
  actionId,
  callRepositoryObjects,
  foldersAndCount,
  repositoryObjects,
  setSelectedFileImport,
  selectedFileImport,
  tree
}: InteractivePartialImportProps) {
  const [expandedItems, setExpandedItems] = useState<string[]>(["/"]);

  const getNameVar = (repoName: string, absolutePath: string) =>
    `${repoName}/${absolutePath}`;

  const getChildFiles = (
    repoName: string,
    directoryPath: string,
    data: RepositoryObject[]
  ): ExtendedRepositoryObject[] =>
    data
      .filter(
        (file) =>
          file.repository.name === repoName &&
          file.name.startsWith(directoryPath)
      )
      .map((file) => ({
        ...file,
        absolutePath: `${file.repository.name}/${file.name}`
      }));
  const onFolderRowClick = (repositoryObject: ExtendedRepositoryObject) => {
    if (repositoryObject.absolutePath.endsWith("/")) {
      callRepositoryObjects({
        variables: {
          repoId: String(repositoryObject.repository.id),
          location: repositoryObject.absolutePath,
          ...(actionId && {
            historicalInput: { historicActionId: actionId }
          })
        }
      });

      const nameVar = getNameVar(
        repositoryObject.repository.name,
        repositoryObject.absolutePath
      );
      setExpandedItems((prev) =>
        prev.includes(nameVar) ? prev : [...prev, nameVar]
      );
    }
  };

  const clickShowMore = async (showMore: RepositoryObjectFolder) => {
    const { data } = await callRepositoryObjects({
      variables: {
        location: showMore.folderName,
        repoId: showMore.repository.id,
        pageNumber: showMore.currentPageNumber + 1
      },
      setTableView: false
    });
    if (
      data &&
      data.some((a2: RepositoryObject) =>
        selectedFileImport.some(
          (s) =>
            s.absolutePath.startsWith(
              `${a2.repository.name}/${showMore.folderName}`
            ) && a2.repository.id === s.repository.id
        )
      )
    ) {
      const mapData = data.map((file: RepositoryObject) => ({
        ...file,
        absolutePath: `${file.repository.name}/${file.name}`
      }));
      setSelectedFileImport((prev: ExtendedRepositoryObject[]) => [
        ...prev,
        ...mapData
      ]);
    }
  };

  const setSelectedFilesFromClick = async (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    repositoryObject: ExtendedRepositoryObject
  ) => {
    event.stopPropagation();
    const repoName = repositoryObject.repository.name;
    const nameVar = getNameVar(repoName, repositoryObject.absolutePath);
    let allNodesSelected = selectedFileImport;
    let files = getChildFiles(
      repoName,
      repositoryObject.absolutePath,
      repositoryObjects
    );

    if (repositoryObject.absolutePath.endsWith("/")) {
      const { data } = await callRepositoryObjects({
        variables: {
          repoId: String(repositoryObject.repository.id),
          location: repositoryObject.absolutePath,
          ...(actionId && {
            historicalInput: { historicActionId: actionId }
          })
        },
        setTableView: false
      });
      files = [
        ...files,
        {
          ...repositoryObject,
          absolutePath: nameVar
        },
        ...getChildFiles(
          repoName,
          repositoryObject.absolutePath,
          data?.getRepositoryObjects?.repositoryObjects || []
        )
      ];

      setExpandedItems((prev) =>
        prev.includes(nameVar) ? prev : [...prev, nameVar]
      );

      if (
        selectedFileImport.some((ai) =>
          files.some((a2) => a2.absolutePath === ai.absolutePath)
        )
      ) {
        allNodesSelected = selectedFileImport.filter(
          (x) => !x.absolutePath.includes(nameVar)
        );
      } else {
        allNodesSelected = allNodesSelected.concat(files);
      }
    } else {
      if (selectedFileImport.some((ai) => ai.absolutePath.includes(nameVar))) {
        allNodesSelected = selectedFileImport.filter(
          (x) => !x.absolutePath.includes(nameVar)
        );
      } else {
        const newNode = { ...repositoryObject, absolutePath: nameVar };
        if (
          !allNodesSelected.some((x) => x.absolutePath === newNode.absolutePath)
        ) {
          allNodesSelected = allNodesSelected.concat(newNode);
        }
      }
    }

    setSelectedFileImport(dedupeFiles(allNodesSelected));
  };

  const dedupeFiles = (allNodes: any[]) => {
    return allNodes.filter(
      (node, index, self) =>
        self.findIndex((n) => n.absolutePath === node.absolutePath) === index
    );
  };

  const renderTreeNodes = (
    data: ExtendedRepositoryObject[],
    repoName: string = ""
  ) =>
    data.map((item) => {
      const isFolder = item.absolutePath.endsWith("/");
      const nameVar = getNameVar(repoName, item.absolutePath);

      const showMore: RepositoryObjectFolder | undefined = isFolder
        ? foldersAndCount.find(
            (folder) =>
              folder.folderName === item.absolutePath &&
              item.content &&
              folder.count > item.content.length &&
              folder.repository.id === item.repository.id
          )
        : undefined;

      const isChecked = selectedFileImport.some(
        (x) => x.absolutePath === nameVar
      );

      const label = isFolder ? (
        <div onClick={() => onFolderRowClick(item)}>
          <BpCheckbox
            aria-label="Select Folder"
            checked={isChecked}
            sx={{ "& .MuiSvgIcon-root": { fontSize: 15 } }}
            className="folder_select_checkbox"
            onClick={(event: MouseEvent<HTMLButtonElement>) =>
              setSelectedFilesFromClick(event, item)
            }
          />
          <FolderIcon className="grey_color folder_icon" />
          {item.name}
        </div>
      ) : (
        <div
          onClick={(event: MouseEvent<HTMLDivElement>) => {
            setSelectedFilesFromClick(event, item);
          }}
        >
          <BpCheckbox
            aria-label="Select File"
            checked={isChecked}
            sx={{ "& .MuiSvgIcon-root": { fontSize: 15 } }}
            className="file_select_checkbox"
          />
          <FileIcon
            fileName={item.name}
            overallValidityStatus={item?.validity?.overallValidityStatus}
            fontSize="small"
            absolutePath={item.name}
          />
          {item.name}
        </div>
      );

      if (item.content && item.content.length > 0) {
        return (
          <StyledTreeItem
            key={item.absolutePath}
            itemId={nameVar}
            data-testid={"treeItem_" + item.absolutePath}
            data-absolutepath={item.absolutePath}
            data-repoid={item.repository.id}
            data-reponame={item.repository.name}
            label={label}
          >
            {renderTreeNodes(item.content, repoName)}
            {showMore && (
              <a
                className="btn ms-4 text-primary"
                onClick={() => clickShowMore(showMore)}
              >
                Show {showMore.count - item.content.length} more...
              </a>
            )}
          </StyledTreeItem>
        );
      }

      return (
        <StyledTreeItem
          itemId={nameVar}
          key={item.absolutePath}
          data-absolutepath={item.absolutePath}
          data-repoid={item.repository.id}
          data-reponame={item.repository.name}
          label={label}
        >
          {isFolder && (
            <StyledTreeItem
              key={"innerFolder" + item.absolutePath}
              itemId={"innerFolder" + item.absolutePath}
              label={<></>}
            />
          )}
        </StyledTreeItem>
      );
    });

  const handleToggle = async (event: any, nodeIds: any) => {
    event.persist();
    const iconClicked = event.target.closest(".MuiTreeItem-root");
    const labelClicked = iconClicked
      ?.querySelector("svg")
      ?.getAttribute("data-testid");

    if (iconClicked && labelClicked === "AddBoxOutlinedIcon") {
      const absolutePath = iconClicked.getAttribute("data-absolutepath");
      const repoId = iconClicked.getAttribute("data-repoid");
      const repoName = iconClicked.getAttribute("data-reponame");

      if (absolutePath.endsWith("/")) {
        const { data } = await callRepositoryObjects({
          variables: {
            repoId,
            location: absolutePath
          },
          setTableView: false
        });

        if (
          Array.isArray(selectedFileImport) &&
          selectedFileImport.some(
            (selected: ExtendedRepositoryObject) =>
              selected.name === absolutePath &&
              selected.repository.id === repoId
          )
        ) {
          const childFiles = getChildFiles(
            repoName,
            absolutePath,
            data?.getRepositoryObjects?.repositoryObjects || []
          );
          console.log(childFiles, "child files");
          setSelectedFileImport((prev: ExtendedRepositoryObject[]) => [
            ...dedupeFiles([...prev, ...childFiles])
          ]);
        }
      }
    }
    if (iconClicked) {
      setExpandedItems(nodeIds);
    }
  };

  return (
    <>
      {tree?.map((treeItem) => (
        <SimpleTreeView
          key={`${treeItem.repository.name}-index`}
          slots={{
            expandIcon: AddBoxOutlinedIcon,
            collapseIcon: IndeterminateCheckBoxOutlinedIcon
          }}
          expandedItems={expandedItems}
          className="mt-2 tree-view text-break"
          disableSelection
          onExpandedItemsChange={handleToggle}
        >
          <StyledTreeItem
            key="/"
            itemId="/"
            data-absolutepath={treeItem.repository.name + "/"}
            data-repoid={treeItem.repository.id}
            data-reponame={treeItem.repository.name}
            label={
              <>
                <CenterFocusStrongIcon className="grey_color" />{" "}
                {treeItem.repository.name}
              </>
            }
          >
            {renderTreeNodes(
              treeItem.repositoryObjects,
              treeItem.repository.name
            )}
          </StyledTreeItem>
        </SimpleTreeView>
      ))}
    </>
  );
}
